import type { SocketType } from "../../types";
import { FLOW_IN_SOCKET_ID } from "../../types";
import {
  buildDynamicSockets,
  effectiveSocketIdVersion,
  supportsSocketIdV2,
} from "./dynamicSocketId";
import type { DynamicSocketDefinition } from "./socketDefinition";
import { configEntryList, prettify } from "./socketDefinition";

/**
 * Generate input sockets for the code_sandbox step from its declared input keys.
 * Returns an empty array when `inputKeys` is missing or empty — callers must
 * treat that as "no input sockets" (not a cue to invent a schema "Value"
 * socket). Runtime validation still uses the open-ended `z.record()` schema.
 */
export interface ResolvedCodeSandboxSockets {
  sockets: DynamicSocketDefinition[];
  /**
   * Socket id for each declared key.
   *
   * `key` is NOT the socket id here — it is the variable name the user's code
   * reads (`input.rows`) and returns. The sandbox is handed its input keyed by
   * `key` and returns its result keyed by `key`, so the handler remaps in both
   * directions. A mistake either way surfaces as `undefined` inside user code
   * with nothing raised, which is why this map is resolved rather than assumed.
   */
  socketIdByKey: Map<string, string>;
}

function resolveKeyedSockets(
  config: Record<string, unknown>,
  entries: Array<{ key: string; socket: Omit<DynamicSocketDefinition, "id"> }>,
  reserved: readonly string[],
): ResolvedCodeSandboxSockets {
  const { sockets, assignedIds } = buildDynamicSockets({
    reserved,
    version: effectiveSocketIdVersion("code_sandbox", config),
    markedFallback: supportsSocketIdV2("code_sandbox"),
    entries: entries.map((e) => ({ name: e.key, socket: e.socket })),
  });

  const socketIdByKey = new Map<string, string>();
  entries.forEach((entry, index) => {
    const id = assignedIds[index];
    if (id !== null) socketIdByKey.set(entry.key, id);
  });

  return { sockets, socketIdByKey };
}

export function resolveCodeSandboxInputSockets(
  config: Record<string, unknown>,
): ResolvedCodeSandboxSockets {
  const inputKeys = configEntryList<{
    key?: unknown;
    type?: unknown;
    description?: unknown;
    required?: unknown;
  }>(config.inputKeys);

  return resolveKeyedSockets(
    config,
    inputKeys
      .filter(
        (
          k,
        ): k is {
          key: string;
          type?: unknown;
          description?: unknown;
          required?: unknown;
        } => typeof k.key === "string" && k.key.length > 0,
      )
      .map((k) => ({
        key: k.key,
        socket: {
          name: prettify(k.key),
          type: (typeof k.type === "string" ? k.type : "any") as SocketType,
          required: typeof k.required === "boolean" ? k.required : true,
          description:
            typeof k.description === "string"
              ? k.description
              : `Input: ${k.key}`,
        },
      })),
    // The engine strips `__flow_control_in__` before the handler runs, so a
    // declared input of that name would always reach the sandbox undefined.
    [FLOW_IN_SOCKET_ID],
  );
}

export function generateCodeSandboxInputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  return resolveCodeSandboxInputSockets(config).sockets;
}

/**
 * Generate output sockets for the code_sandbox step from its declared output keys.
 */
export function resolveCodeSandboxOutputSockets(
  config: Record<string, unknown>,
): ResolvedCodeSandboxSockets {
  const outputKeys = configEntryList<{
    key?: unknown;
    type?: unknown;
    description?: unknown;
  }>(config.outputKeys);

  return resolveKeyedSockets(
    config,
    outputKeys
      .filter(
        (k): k is { key: string; type?: unknown; description?: unknown } =>
          typeof k.key === "string" && k.key.length > 0,
      )
      .map((k) => ({
        key: k.key,
        socket: {
          name: prettify(k.key),
          type: (typeof k.type === "string" ? k.type : "any") as SocketType,
          required: true,
          description:
            typeof k.description === "string"
              ? k.description
              : `Output: ${k.key}`,
        },
      })),
    [],
  );
}

export function generateCodeSandboxOutputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  return resolveCodeSandboxOutputSockets(config).sockets;
}
